import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { siteInfo, trendingList } from "@/mocks/news";

const navItems = [
  { label: "首页", to: "/" },
  { label: "今日速递", to: "/#feed" },
  { label: "事件时间线", to: "/#timeline" },
  { label: "热门人物", to: "/#figures" },
  { label: "快报专栏", to: "/category/briefs" },
  { label: "关于我们", to: "/about" },
];

export default function SiteHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [keyword, setKeyword] = useState("");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.hash]);

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    if (!value) {
      navigate("/category/all");
      return;
    }
    navigate(`/category/all?q=${encodeURIComponent(value)}`);
    setKeyword("");
  };

  const isActive = (to: string) => {
    if (to.startsWith("/#")) return false;
    if (to === "/") return location.pathname === "/";
    return location.pathname.startsWith(to);
  };

  return (
    <header className="sticky top-0 z-50">
      <div className="bg-foreground-950 text-background-100">
        <div className="mx-auto flex h-9 max-w-[1400px] items-center gap-3 px-4 md:px-8">
          <span className="label-font hidden shrink-0 items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-accent-300 sm:flex">
            <i className="ri-flashlight-fill text-accent-400" />
            速递进行中
          </span>
          <div className="relative flex-1 overflow-hidden">
            <div className="flex w-max animate-marquee">
              {[0, 1].map((loop) => (
                <div key={loop} className="flex shrink-0 items-center">
                  {trendingList.map((item) => (
                    <Link
                      key={`${loop}-${item.rank}`}
                      to={`/article/${item.articleId}`}
                      className="mr-8 whitespace-nowrap text-xs text-background-100/80 transition-colors hover:text-accent-300"
                    >
                      <span className="mr-1.5 text-primary-400">#{item.rank}</span>
                      {item.title}
                    </Link>
                  ))}
                </div>
              ))}
            </div>
          </div>
          <span className="hidden shrink-0 text-[11px] text-background-100/60 lg:block">
            {siteInfo.updateNote}
          </span>
        </div>
      </div>

      <div
        className={`border-b transition-all duration-300 ${
          scrolled
            ? "border-background-200 bg-background-50/95 backdrop-blur-md"
            : "border-transparent bg-background-50"
        }`}
      >
        <div className="mx-auto flex h-16 max-w-[1400px] items-center justify-between gap-4 px-4 md:h-[72px] md:px-8">
          <Link to="/" className="flex shrink-0 items-center gap-2.5">
            <span className="relative flex h-10 w-10 items-center justify-center rounded-lg bg-primary-500">
              <i className="ri-chat-quote-fill text-lg text-background-50" />
              <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-accent-400 ring-2 ring-background-50" />
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading text-xl font-black tracking-wide text-foreground-950">
                {siteInfo.name}
              </span>
              <span className="mt-0.5 hidden text-[10px] tracking-[0.22em] text-foreground-500 sm:block">
                独家黑料速递
              </span>
            </span>
          </Link>

          <nav className="hidden items-center gap-1 lg:flex">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                  isActive(item.to)
                    ? "bg-primary-100 text-primary-700"
                    : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <form
              onSubmit={handleSearch}
              className="hidden items-center rounded-full border border-background-300 bg-background-100/70 pl-3 pr-1 transition-colors focus-within:border-primary-400 xl:flex"
            >
              <span className="flex h-8 w-5 items-center justify-center">
                <i className="ri-search-line text-sm text-foreground-500" />
              </span>
              <input
                type="search"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索速递条目"
                aria-label="搜索速递条目"
                className="h-8 w-36 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
              />
              <button
                type="submit"
                aria-label="开始搜索"
                className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-full bg-primary-500 text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-arrow-right-line text-xs" />
              </button>
            </form>

            <Link
              to="/#subscribe"
              className="hidden whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 md:block"
            >
              订阅速递
            </Link>

            <button
              type="button"
              onClick={() => setMenuOpen((prev) => !prev)}
              aria-label="打开导航菜单"
              aria-expanded={menuOpen}
              className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-800 transition-colors hover:bg-background-100 lg:hidden"
            >
              <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} />
            </button>
          </div>
        </div>

        {menuOpen && (
          <div className="border-t border-background-200 bg-background-50 lg:hidden">
            <div className="mx-auto max-w-[1400px] px-4 py-4 md:px-8">
              <form
                onSubmit={handleSearch}
                className="mb-3 flex items-center rounded-md border border-background-300 bg-background-100 pl-3"
              >
                <span className="flex h-10 w-5 items-center justify-center">
                  <i className="ri-search-line text-sm text-foreground-500" />
                </span>
                <input
                  type="search"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索速递条目"
                  aria-label="搜索速递条目"
                  className="h-10 flex-1 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
                />
                <button
                  type="submit"
                  className="h-10 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 text-sm font-semibold text-background-50"
                >
                  搜索
                </button>
              </form>
              <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
                {navItems.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={`rounded-md px-3 py-2.5 text-sm font-medium ${
                      isActive(item.to)
                        ? "bg-primary-100 text-primary-700"
                        : "text-foreground-700 hover:bg-background-100"
                    }`}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}